// disable auto-story
import { Gtm } from '@nrdy-marketing-engine/gtm';
import Head from 'next/head';
import { MetadataProps } from '../@types/schema';

const absoluteUrl = (path: string) =>
  path.match(/^http/) ? path : process.env.NEXT_PUBLIC_VT_WWW_ENDPOINT + path;

const NOINDEX = 'noindex, nofollow';

const AppleiTunesAppMeta = ({ appleiTunesApp }: MetadataProps) => {
  if (!appleiTunesApp) return null;

  return (
    <Head>
      <meta content="app-id=866311648" name="apple-itunes-app" />
    </Head>
  );
};

const OpenGraph = ({ description, title, canonical, openGraph = {} }: MetadataProps) => {
  const ogTitle = title.split(/ [|-] Varsity Tutors/)[0] || title;
  return (
    <Head>
      <meta content={ogTitle} property="og:title" />
      <meta content={description} property="og:description" />
      <meta content={openGraph.type || 'Website'} property="og:type" />
      {canonical && <meta content={absoluteUrl(canonical)} property="og:url" />}
      {openGraph.image && <meta content={openGraph.image} property="og:image" />}
    </Head>
  );
};

const Robots = ({ indexable }: MetadataProps) => {
  const indexingEnabled = process.env.NEXT_PUBLIC_INDEXABLE;
  const showNoIndex = !indexingEnabled || !indexable;
  const showProdRobots = !indexingEnabled;
  return (
    <Head>
      {showNoIndex && <meta content={NOINDEX} name="robots" />}
      {showProdRobots && <meta content={indexable ? '' : NOINDEX} name="production-robots" />}
    </Head>
  );
};

const Canonicals = ({ canonical, alternates }: MetadataProps) => (
  <Head>
    {canonical && <link href={absoluteUrl(canonical)} rel="canonical" />}
    {alternates &&
      alternates.map(({ href, hrefLang }) => (
        <link href={absoluteUrl(href)} hrefLang={hrefLang} key={hrefLang} rel="alternate" />
      ))}
  </Head>
);

const TitleAndDescription = ({ title, description }: MetadataProps) => (
  <Head>
    <title>{title}</title>
    <meta content={description} name="description" />
  </Head>
);

/**
 * Sets up the `<Head>` of a page with typical settings for Nerdy Marketing Engine.
 */
function NerdyHead({ metadata }: { metadata: MetadataProps }) {
  const { lang, nspSlug } = metadata;
  const gtmData = { description: 'Tutoring' };

  return (
    <>
      <Gtm data={gtmData} />
      <TitleAndDescription {...metadata} />
      <Canonicals {...metadata} />
      <OpenGraph {...metadata} />
      <Robots {...metadata} />
      <AppleiTunesAppMeta {...metadata} />
      <Head>
        <meta content={lang || 'en'} key="language" name="language" />
        {nspSlug && <meta content={nspSlug} name="nsp-slug" />}
      </Head>
    </>
  );
}

export default NerdyHead;
